import Container from 'components/ui/Container';
import PrimaryButton from 'components/ui/PrimaryButton';
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useAssignmentDetailQuery,
  useCreateMCQAssignmentMutation,
  useDeleteMCQAnswerAssignmentMutation,
  useDeleteMCQAssignmentMutation,
  useUpdateMCQAssignmentMutation
} from 'redux/reducers/assignment';
import { FormikProvider, useFormik } from 'formik';
import {
  MCQSchemaValidations,
  MCQInitialValues,
  MCQEditSchemaValidations
} from './AssignmentSchema';
import Modal from 'components/ui/Modal/modal';
import SecondaryButton from 'components/ui/SecondaryButton';
import useToggle from 'hooks/useToggle';
import MCQForm from './MCQForm';
import { toast } from 'react-toastify';
import ConfirmationModal from 'components/ui/ConfirmationModal/confirmation-modal';
import AssignmentEditform from './AssignmentEditform';
import { backendURL } from 'redux/reducers/settings';

const AssignmentDetail = () => {
  const [deleteModal, toggleDeleteModal] = useToggle(false);
  const [editModal, toggleEditModal] = useToggle(false);
  const [formModal, setFormModal] = useToggle(false);

  const [assignmentInitialFormState, setAssignmentInitialForm] =
    React.useState<any>(MCQInitialValues);
  const [deletingEvaluationState, setDeletingEvaluation] = useState<number | null>(null);
  const [editMCQState, setEditMCQState] = useState<any | null>(null);

  const { id } = useParams();
  const { data: getAssignment, isLoading } = useAssignmentDetailQuery({ id }, { skip: !id });

  const [CreateMCQ, { isLoading: creatingMCQ }] = useCreateMCQAssignmentMutation();
  const [updateMCQ, { isLoading: isUpdatingMCQ }] = useUpdateMCQAssignmentMutation();
  const [deleteMCQ, { isLoading: isDeletingMCQ }] = useDeleteMCQAssignmentMutation();

  const [deleteMCQAns, { isLoading: isDeletingMCQans }] = useDeleteMCQAnswerAssignmentMutation();

  const MCQdelete = (id: number) => {
    setDeletingEvaluation(id);
    toggleDeleteModal();
  };

  const onDeleteConfirm = () => {
    deletingEvaluationState &&
      deleteMCQ(deletingEvaluationState)
        .unwrap()
        .then(() => {
          toast.success('Deleted Successfully');
          setAssignmentInitialForm(MCQInitialValues);
          toggleDeleteModal();
        })
        .catch(() => toast.error('Error'));
  };

  const setMCQansDeleteID = (id: number) => {
    deleteMCQAns(id)
      .unwrap()
      .then(() => toast.success('Option Deleted'))
      .catch(() => toast.error('Error'));
  };

  const editOption = (mcq: any) => {
    const payload={
      ...mcq,
      order:mcq.mcq_order
    }
    setEditMCQState(payload);
    toggleEditModal();
  };

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: MCQSchemaValidations,
    initialValues: assignmentInitialFormState,
    onSubmit: (values, { resetForm }) => {
      const Data = {
        assignment_id: getAssignment?.data.tms_assignment_id,
        ...values.mcq_id[0]
      };
      CreateMCQ(Data)
        .unwrap()
        .then(() => {
          setAssignmentInitialForm(MCQInitialValues);
          resetForm();
          toast.success('Created Successfully');
          setFormModal(false);
        })
        .catch((err) => {
          if (err.data?.difficulty_level) toast.error('Difficulty level should be 1 to 10!');
        });
    }
  });

  const editformik = useFormik({
    enableReinitialize: true,
    validationSchema: MCQEditSchemaValidations,
    initialValues: editMCQState,
    onSubmit: (values, { resetForm }) => {
      const Data = {
        mcq_id: editMCQState?.mcq_id,
        ...values
      };
      updateMCQ(Data)
        .unwrap()
        .then(() => {
          setEditMCQState(MCQInitialValues);
          resetForm();
          toast.success('Updated Successfully');
          toggleEditModal();
        })
        .catch((err) => {
          if (err.data?.difficulty_level) toast.error('Difficulty level should be 1 to 10!');
        });
    }
  });

  return (
    <Container title="Assignment Details">
      {/* Modals */}
      <ConfirmationModal
        isOpen={deleteModal}
        onClose={toggleDeleteModal}
        onConfirm={onDeleteConfirm}
        title="Delete Confirmation"
        isLoading={isDeletingMCQ}
      >
        <div className="p-3 text-center">
          <i className="ri-delete-bin-line text-danger display-5"></i>
          <h4 className="mt-3">Are you sure?</h4>
          <p className="text-muted">This action will permanently remove this MCQ.</p>
        </div>
      </ConfirmationModal>

      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Modal
            title="Create New MCQ"
            isModalOpen={formModal}
            closeModal={() => { setFormModal(); formik.resetForm(); }}
            renderFooter={() => (
              <div className="hstack gap-2 justify-content-end">
                <SecondaryButton onClick={setFormModal} title="Close" />
                <PrimaryButton title="Submit" type="submit" isLoading={creatingMCQ} />
              </div>
            )}
          >
            <MCQForm />
          </Modal>
        </form>
      </FormikProvider>

      <FormikProvider value={editformik}>
        <form onSubmit={editformik.handleSubmit}>
          <Modal
            title="Edit MCQ"
            isModalOpen={editModal}
            closeModal={() => { toggleEditModal(); editformik.resetForm(); }}
            renderFooter={() => (
              <div className="hstack gap-2 justify-content-end">
                <SecondaryButton onClick={toggleEditModal} title="Close" />
                <PrimaryButton title="Update" type="submit" isLoading={isUpdatingMCQ} />
              </div>
            )}
          >
            <AssignmentEditform />
          </Modal>
        </form>
      </FormikProvider>

      {/* Main Content */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <span className="badge bg-primary-subtle text-primary mb-2">Assignment Overview</span>
                  <h3 className="fw-bold text-dark">{getAssignment?.data?.assignment_title}</h3>
                </div>
                <PrimaryButton
                  title="Add MCQ"
                  iconName="ri-add-line"
                  type="button"
                  onClick={() => setFormModal()}
                />
              </div>
              
              <div className="row g-4">
                <div className="col-md-8">
                  <h6 className="text-muted fw-semibold text-uppercase small">Description</h6>
                  <p className="text-secondary">{getAssignment?.data?.assignment_description || 'No description provided.'}</p>
                </div>
                <div className="col-md-4">
                  {getAssignment?.data?.assignment_file?<a  href={backendURL+ getAssignment?.data?.assignment_file} target='_blank'>

                  
                  <h6 className="text-muted fw-semibold text-uppercase small">Attached File</h6>
                  <div className="d-flex align-items-center p-2 border rounded bg-light">
                    <i className="ri-file-text-line fs-3 me-2 text-primary"></i>
                      File
                  </div>
                  </a>
                     :'No files attached'}
                     </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex align-items-center mb-3">
        <h5 className="fw-bold mb-0">Question Bank</h5>
        <span className="badge bg-secondary ms-2">{getAssignment?.data?.multi_choice_question_details?.length || 0} Questions</span>
      </div>

      {/* MCQ List */}
      <div className="row">
        {getAssignment?.data?.multi_choice_question_details.map((mcq: any, index: number) => (
          <div className="col-12 mb-4" key={mcq.mcq_id || index}>
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-bottom-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                   <div className="avatar-sm bg-light text-primary rounded-circle d-flex align-items-center justify-content-center fw-bold me-3" style={{width: '35px', height: '35px'}}>
                      {index + 1}
                   </div>
                   <span className="badge bg-info-subtle text-info me-2">Marks: {mcq.marks}</span>
                   <span className="badge bg-warning-subtle text-warning">Level: {mcq.difficulty_level}</span>
                </div>
                <div className="hstack gap-2">
                  <button className="btn btn-soft-secondary btn-sm" onClick={() => editOption(mcq)}>
                    <i className="ri-edit-2-line"></i> Edit
                  </button>
                  <button className="btn btn-soft-danger btn-sm" onClick={() => MCQdelete(mcq.mcq_id)}>
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
              
              <div className="card-body px-4 pb-4">
                <h5 className="mb-4 text-dark-emphasis fw-medium">{mcq.question}</h5>
                
                <div className="row g-3">
                  {mcq.options.map((opt: any, index2: number) => (
                    <div className="col-md-6 col-lg-3" key={opt.mcq_answer_id || index2}>
                      <div className={`p-3 rounded border h-100 position-relative ${opt.is_correct ? 'border-success bg-success-subtle' : 'border-light bg-light'}`}>
                        <div className="d-flex justify-content-between">
                           <div className="form-check">
                              <input 
                                className="form-check-input" 
                                type="checkbox" 
                                checked={opt.is_correct} 
                                readOnly 
                              />
                              <label className="form-check-label fw-medium small">
                                {opt.answer}
                              </label>
                           </div>
                           <button 
                              className="btn btn-link text-danger p-0 border-0" 
                              onClick={() => setMCQansDeleteID(opt.mcq_answer_id)}
                              title="Delete option"
                            >
                              <i className="ri-close-circle-fill"></i>
                           </button>
                        </div>
                        {opt.is_correct && (
                          <span className="badge bg-success position-absolute top-0 end-0 m-1" style={{fontSize: '8px'}}>CORRECT</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Container>
  );
};

export default AssignmentDetail;